/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { object, omit } from 'underscore';
import {
  Dropdown,
  Option,
  OptionGroup,
  Label,
  makeStyles,
  tokens,
  typographyStyles,
} from '@fluentui/react-components';
import { countryList, countryCodeMap } from './config';

const useStyles = makeStyles({
  filterItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  filterItemLabel: {
    fontStyle: 'normal',
    ...typographyStyles.caption1Strong,
    color: '#605E5C',
  },
  filterItemDropdown: {
    border: '1px solid #B3B3B3',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    width: '259px',
  },
});

const CountryFilterComponent = memo((props) => {
  const {
    countryCodeList,
    searchParams,
    setSearchParams,
  } = props;
  const classes = useStyles();

  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    if (countryCodeList.length === 0) {
      setAllSelected(true);
    }
  }, []);

  const onCountrySelect = useCallback((ev, data) => {
    let newSearchParams = object([...searchParams]);
    const { selectedOptions } = data;
    if (data.optionValue === 'All countries') {
      setAllSelected(!allSelected);
      setSearchParams(omit(newSearchParams, 'ctrs'));
      return;
    }
    setAllSelected(false);
    const selectedCountryCodes = selectedOptions.map((countryName) => {
      const countryCode = countryCodeMap[countryName];
      return countryCode;
    }).filter(code => code !== undefined);
    const selectedCountryCodesString = selectedCountryCodes.join(',');

    if (selectedCountryCodesString !== '') {
      newSearchParams.ctrs = selectedCountryCodesString;
    } else {
      newSearchParams = omit(newSearchParams, 'ctrs');
    }

    setSearchParams(newSearchParams);
  }, [allSelected, searchParams, setSearchParams]);

  const countryOptions = useMemo(() => countryList.map(country => (
    <Option key={country.code}>
      {country.value}
    </Option>
  )), [allSelected]);

  const selectedCountries = allSelected ? ['All countries'] : countryCodeList.map((countryCode) => {
    const country = countryList.find(c => c.code === countryCode);
    return country ? country.value : null;
  });

  const displayString = allSelected ? 'All countries' : selectedCountries.join(', ');

  return (
    <div className={classes.filterItem}>
      <Label className={classes.filterItemLabel} aria-labelledby="Country">Country</Label>
      <Dropdown
        className={classes.filterItemDropdown}
        selectedOptions={selectedCountries}
        value={displayString}
        multiselect
        onOptionSelect={onCountrySelect}
        positioning="below"
        aria-label="Country dropdown"
      >
        <OptionGroup label="Select countries">
          { countryOptions }
        </OptionGroup>
      </Dropdown>
    </div>
  );
}, (prevProps, nextProps) => prevProps.selectedCountries === nextProps.selectedCountries
&& prevProps.searchParams === nextProps.searchParams);

CountryFilterComponent.propTypes = {
  countryCodeList: PropTypes.arrayOf(PropTypes.string).isRequired,
  searchParams: PropTypes.shape({
    get: PropTypes.func.isRequired,
  }).isRequired,
  setSearchParams: PropTypes.func.isRequired,
};

export const CountryFilter = CountryFilterComponent;
